/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from "react";
import * as XLSX from "xlsx";
import {
  Box,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
} from "@mui/material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import UploadChemicalAnalysisDataDialog from "../../components/UploadChemicalAnalysisDataDialog";
import UploadSpectraDataDialog from "../../components/UploadSpectraDataDialog";
import {
  ChemicalDataBaseFields,
  ChemicalFields,
} from "../../../types/chemicals";
import UploadRawSpectraFileDialog from "../../components/UploadRawSpectraFile/UploadRawSpectraFileDialog";
import GeneralPopoverWrapper from "../../components/Common/GeneralPopoverWrapper";
import AskAnythingButton from "../../components/UserAssistance/AskAnythingButton";
import { useTranslation } from "react-i18next";
import companyService from "../../../service/company.service";
import { useAppContext } from "../../../contexts/useStorageContext";
import { actionTypes } from "../../../types/state";
import { showSnackbar } from "../../../service/snackbar.service";
import { SpectraDeviceNames } from "../../../types/sample";
export default function ActionButtonGroup({
  size = "medium",
  lastChemSsid,
  lastSpecSsid,
  contactOnly = false,
}: {
  size?: "small" | "medium";
  lastChemSsid?: number;
  lastSpecSsid?: number;
  /** Mobile samples mode: hide chem/upload actions. */
  contactOnly?: boolean;
}) {
  const { dispatch, state } = useAppContext();
  const { t } = useTranslation();
  const [openChemicalUploadDialog, setOpenChemicalUploadDialog] =
    React.useState(false);
  const [openSpectraUploadDialog, setOpenSpectraUploadDialog] =
    React.useState(false);
  const [openRawSpectraUploadDialog, setOpenRawSpectraUploadDialog] =
    React.useState(false);
  const handleDownloadChemTemplate = React.useCallback(() => {
    const jsonData: any = {};
    ChemicalDataBaseFields.map((field) => {
      jsonData[field.fieldName] =
        field.dbField == "companyUid"
          ? lastChemSsid
            ? lastChemSsid + 1
            : 1
          : "";
    });

    ChemicalFields.map((field) => {
      jsonData[field.fieldName] = "";
    });
    const ws = XLSX.utils.json_to_sheet([jsonData]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, `chem_data.xlsx`);
  }, [lastChemSsid]);
  const [refreshingMyCompanyProfile, setRefreshingMyCompanyProfile] = React.useState(false);

  const handleRefreshMyCompanyProfile = async () =>{
    try{
      setRefreshingMyCompanyProfile(true);
      const result = await companyService.getMyCompany();
      if (result?.data) {
        dispatch!({
          type: actionTypes.setMyCompanyProfile,
          value: result.data,
        });
      }
    } catch (error) {
      showSnackbar("danger", "", "Could not refresh your company profile.");
    } finally {
      setRefreshingMyCompanyProfile(false);
    }
  }
  return (
    <Stack direction="row" spacing={1}>
      {!contactOnly && (
        <>
          <Button
            size={size}
            sx={{
              padding: { xs: "4px 8px" },
              minHeight: "32px",
              display: { xs: "none", md: "flex" },
            }}
            onClick={() => handleDownloadChemTemplate()}
            variant="outlined"
            startIcon={<SaveAltIcon />}
          >
            <Box sx={{ mr: "4px" }}>{t("Chem Template")}</Box>
          </Button>
          <IconButton
            size={size}
            sx={{ display: { xs: "flex", md: "none" } }}
            onClick={() => handleDownloadChemTemplate()}
          >
            <SaveAltIcon />
          </IconButton>
          <GeneralPopoverWrapper
        triggerElement={
          <>
            <Button
              size={size}
              onClick={() => handleRefreshMyCompanyProfile()}
              sx={{
                display: { xs: "none", md: "flex" },
                padding: { xs: "4px 8px" },
                minHeight: "32px",
              }}
              variant="outlined"
              startIcon={<FileUploadIcon />}
            >
              <Box sx={{ display: { xs: "none", md: "block" }, mr: "4px" }}>
                {t("Upload")}
              </Box>
            </Button>
            <IconButton size={size} sx={{ display: { xs: "flex", md: "none" } }}>
              <FileUploadIcon />
            </IconButton>
          </>
        }
        popoverContent={
          <Box py={1}>
            <MenuItem onClick={() => setOpenChemicalUploadDialog(true)}>
              <ListItemIcon>
                <FileUploadIcon />
              </ListItemIcon>
              {`${t("Upload Chem Data")} (.xlsx, .csv)`}
            </MenuItem>
            <MenuItem onClick={() => setOpenSpectraUploadDialog(true)}>
              <ListItemIcon>
                <FileUploadIcon />
              </ListItemIcon>
              {`${t("Upload Spectra Data")} (.xlsx, .csv)`}
            </MenuItem>
            <MenuItem disabled={
              refreshingMyCompanyProfile ||
              (!state.myCompanyProfile?.spectraDeviceNames?.includes(SpectraDeviceNames.BlueSun) &&
              !state.myCompanyProfile?.spectraDeviceNames?.includes(SpectraDeviceNames.Foss))
            } onClick={() => setOpenRawSpectraUploadDialog(true)}>
              <ListItemIcon>
                <FileUploadIcon />
              </ListItemIcon>
              <ListItemText
              primary={`${t("Upload Raw Spectra Data")} (.nir)`}
              secondary={<Box sx={{ fontSize:"12px",
                color:(!state.myCompanyProfile?.spectraDeviceNames?.includes(SpectraDeviceNames.BlueSun) &&
                !state.myCompanyProfile?.spectraDeviceNames?.includes(SpectraDeviceNames.Foss))?"error.main":undefined
              }}>{refreshingMyCompanyProfile ? t("Checking...") :
                (!state.myCompanyProfile?.spectraDeviceNames?.includes(SpectraDeviceNames.BlueSun) &&
                !state.myCompanyProfile?.spectraDeviceNames?.includes(SpectraDeviceNames.Foss)) ? t("Ask Super Admin to add Benchtop device to your company") : undefined}</Box>}
              />
            </MenuItem>
          </Box>
        }
      />
        </>
      )}
      <Box
        sx={{
          display: { xs: "none", sm: "inline-flex" },
          alignItems: "center",
        }}
      >
        <AskAnythingButton compact />
      </Box>
      {openChemicalUploadDialog && (
        <UploadChemicalAnalysisDataDialog
          onClose={() => setOpenChemicalUploadDialog(false)}
        />
      )}
      {openSpectraUploadDialog && (
        <UploadSpectraDataDialog
          onClose={() => setOpenSpectraUploadDialog(false)}
        />
      )}
      {openRawSpectraUploadDialog && (
        <UploadRawSpectraFileDialog
          onClose={() => setOpenRawSpectraUploadDialog(false)}
        />
      )}
    </Stack>
  );
}
